import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
  AccountDeviceActions,
  AccountDeviceFacade
} from "@youm-front/account-device/data-access";
import { combineLatest } from "rxjs";
import {
  catchError,
  concatMap,
  debounceTime,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap
} from "rxjs/operators";
import { AccountCamMicActions } from "../actions";
import {
  AccountCamMicStreamFactoryService,
  AccountCamMicStreamService
} from "../services";

@Injectable()
export class AccountCamMicEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly accountCamMicStreamFactoryService: AccountCamMicStreamFactoryService,
    private readonly accountCamMicStreamService: AccountCamMicStreamService,
    private readonly accountDeviceFacade: AccountDeviceFacade
  ) {}

  accountCamMic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountCamMicActions.streamInit),
      mergeMap(() =>
        this.accountDeviceFacade.ready$.pipe(
          filter((_) => _),
          take(1)
        )
      ),
      switchMap(() =>
        combineLatest([
          this.accountDeviceFacade.audioInputDeviceId$,
          this.accountDeviceFacade.videoInputDeviceId$,
          this.accountDeviceFacade.videoInputDeviceIsFlipped$,
          this.accountDeviceFacade.videoInputResolution$
        ]).pipe(
          debounceTime(0),
          map(
            ([
              audioInputDeviceId,
              videoInputDeviceId,
              videoInputDeviceIsFlipped,
              videoInputResolution
            ]) => ({
              // Take it directly on device change to avoid CamMic stream update (and flickering) at any track update
              audioInputDeviceDisabled:
                this.accountDeviceFacade.audioInputDeviceDisabled,
              audioInputDeviceId,
              // Take it directly on device change to avoid CamMic stream update (and flickering) at any track update
              videoInputDeviceDisabled:
                this.accountDeviceFacade.videoInputDeviceDisabled,
              videoInputDeviceId,
              videoInputDeviceIsFlipped,
              videoInputResolution
            })
          ),
          tap(() => {
            // Next stream may require different settings for the same device (e.g., video resolution).
            // Stop using the device with the current settings by releasing the running tracks.
            this.accountCamMicStreamService.release();
          }),
          concatMap(
            ({
              audioInputDeviceDisabled,
              audioInputDeviceId,
              videoInputDeviceDisabled,
              videoInputDeviceId,
              videoInputDeviceIsFlipped,
              videoInputResolution
            }) => {
              return this.accountCamMicStreamFactoryService
                .create({
                  audioInputDeviceDisabled,
                  audioInputDeviceId,
                  videoInputDeviceDisabled,
                  videoInputDeviceId,
                  videoInputDeviceIsFlipped,
                  videoInputResolution
                })
                .pipe(
                  tap((_) => this.accountCamMicStreamService.set(_)),
                  mergeMap(() => []),
                  catchError((_) => [
                    AccountCamMicActions.streamFailure({ error: _ })
                  ])
                );
            }
          )
        )
      )
    )
  );

  streamInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountCamMicActions.streamInit),
      map(() => AccountDeviceActions.loadDevices())
    )
  );

  streamDestroy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountCamMicActions.streamDestroy),
        tap(() => this.accountCamMicStreamService.release())
      ),
    { dispatch: false }
  );
}
